import { State } from "redux/store";
import classNames from "classnames";
import { connect } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { useEffect, useRef, useState } from "react";

import { FilterItemCountsWithSelect } from "types";
import { useAccordionContext } from "contexts/AccordionContext";
import { formatNumberWithCommas } from "helpers/utility/formatters";
import useAutosizerScrollStatus from "hooks/useAutosizerScrollStatus";
import {
  getHoveredFileState,
  getTreeFolder,
} from "redux/SingleDataset/selectors";

import styles from "./style.module.scss";
import { getTextWidth } from "helpers/utility/utilities";
import FrameIcon from "assets/icons/frameIcon";
import AnimatedBar from "./AnimatedSingleBar";

const MARGIN_TOP = 16;
const HEADER_HEIGHT = 32;
const HEADER_PADDING = 16;

const NoMatchingData = () => (
  <div className={styles.noMatchcontainer}>No Matching Data.</div>
);

const SingleFileDisplay = ({
  index,
  path,
  totalCount,
  isChecked,
  maxFileCount,
  isLoading,
}: any) => {
  const frameCountText = formatNumberWithCommas(totalCount) + " Frames";
  const maxFrameCountText = formatNumberWithCommas(maxFileCount) + " Frames";

  const dynamicFileName = (path: string) => {
    return path.split("/").pop();
  };

  const fileIconClassnames = classNames(styles.fileIcon, {
    [styles.selectedFileIcon]: isChecked,
  });

  const handleFileTitleBoxStyle = () => {
    let maxWidth = `calc(100% - ${getTextWidth(maxFrameCountText)}px - 12px)`;
    return { width: maxWidth };
  };

  const singleFileDisplayClassnames = classNames(styles.singleFileDisplay, {
    [styles.selectedFileDisplay]: isChecked,
  });

  return (
    <AnimatedBar
      className={singleFileDisplayClassnames}
      key={index}
      loading={isLoading}
    >
      <div className={styles.fileDisplayBar}>
        <div className={styles.fileTitleBox} style={handleFileTitleBoxStyle()}>
          <div className={fileIconClassnames}>
            <FrameIcon />
          </div>
          <div
            className={styles.filePath}
            title={dynamicFileName(path)}
            style={{ fontWeight: isChecked ? 500 : 300 }}
          >
            {dynamicFileName(path)}
          </div>
        </div>
        <div className={styles.fileCountBox}>{frameCountText}</div>
      </div>
    </AnimatedBar>
  );
};

const Row = ({ index, style, data, isLoading }: any) => {
  const singleData = data[index];
  const paddingTop = index === 0 || index === data.length - 1 ? "1px" : "0";
  const paddingBottom = index === data.length - 1 ? 0 : "8px";
  return (
    <div
      key={index}
      className={styles.singleFileRow}
      style={{
        ...style,
        padding: `${paddingTop} 16px ${paddingBottom} 16px`,
      }}
    >
      <SingleFileDisplay
        index={index}
        path={singleData.title}
        totalCount={singleData.count}
        isChecked={singleData.selected}
        maxFileCount={Math.max(...data.map((label: any) => label.count))}
        isLoading={isLoading}
      />
    </div>
  );
};

interface FileDisplayContentProps {
  isLoading: boolean;
  searchDisabled: boolean;
  isContentSelected: boolean;
  showNoMatchingData: boolean;
  data: FilterItemCountsWithSelect[];
}

const FileDisplayContent = ({
  data,
  showNoMatchingData,
  isContentSelected,
  isLoading = false,
  searchDisabled,
}: FileDisplayContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement>(null);

  const { setHeightLimits, height: contextHeight } = useAccordionContext();
  const [calculatedHeight, setCalculatedHeight] = useState(contextHeight);
  const { isOverflowing, isAtBottom } = useAutosizerScrollStatus(
    containerRef,
    listRef,
    { dependencyList: [data.length, isContentSelected] }
  );

  const wrapperClassnames = classNames(styles.wrapper, {
    [styles.overflowingWrapper]: isOverflowing && !isAtBottom,
  });

  const extraHeight = searchDisabled
    ? MARGIN_TOP
    : HEADER_HEIGHT + HEADER_PADDING + MARGIN_TOP;

  const itemSize = (index?: number) => {
    if (index === data.length - 1) return 38; // Set a smaller height for the last item
    return 48; // Set a default item height
  };

  // Calculate heights for the accordion
  const calculateHeight = (numBars: number) => {
    const singleBarHeight = 48; // Default item height
    const lastBarHeight = data.length > 1 ? 38 : 48; // Last item height
    const totalHeight = (numBars - 1) * singleBarHeight + lastBarHeight;

    return totalHeight + extraHeight;
  };

  const dynamicWrapperHeight = () => {
    if (isContentSelected) {
      return { height: "100%", maxHeight: "100%" };
    } else if (calculatedHeight) {
      return { height: `${calculatedHeight - extraHeight}px` };
    }

    const fallback = calculateHeight(Math.min(data.length, 5));
    return { height: `${fallback - extraHeight}px` };
  };

  useEffect(() => setCalculatedHeight(contextHeight), [contextHeight]);

  useEffect(() => {
    const initialHeight = calculateHeight(Math.min(data.length, 5));
    const minHeight = calculateHeight(Math.min(data.length, 2));
    const maxHeight = calculateHeight(data.length);

    if (
      calculatedHeight &&
      calculatedHeight >= minHeight &&
      calculatedHeight <= maxHeight
    ) {
      return;
    }
    setHeightLimits({ initialHeight, minHeight, maxHeight });
    //eslint-disable-next-line
  }, [data.length, extraHeight]);

  return showNoMatchingData ? (
    <NoMatchingData />
  ) : (
    <div
      ref={containerRef}
      className={wrapperClassnames}
      style={dynamicWrapperHeight()}
    >
      <AutoSizer>
        {({ height, width }: any) => (
          <List
            outerRef={containerRef}
            innerRef={listRef}
            height={height}
            width={width}
            itemCount={data.length}
            itemSize={itemSize}
            itemData={data}
            className={`List ${styles.fileListContainer}`}
          >
            {({ index, style }) => {
              return (
                <Row
                  data={data}
                  index={index}
                  style={style}
                  isLoading={isLoading}
                />
              );
            }}
          </List>
        )}
      </AutoSizer>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    hoveredFileState: getHoveredFileState(state),
    treeFolder: getTreeFolder(state),
  };
};
export default connect(mapStateToProps)(FileDisplayContent);
